import { useRef, useState, type ReactNode } from "react";
import type { Attachment } from "@lumen/shared";
import { Paperclip, Pencil, Send, Trash2 } from "lucide-react";
import { api } from "../../api";
import { orderedQueueItems } from "../../pages/chat/conversationQueueState";
import { t } from "../../i18n";

export type QueuedMessage = {
  id: string;
  content: string;
  steer?: boolean;
  error?: string;
  options: Record<string, unknown>;
  attachmentIds: string[];
  attachments?: Attachment[];
};

export function MessageQueuePanel({
  conversationId,
  items,
  onChange,
  onEdit,
  editor,
}: {
  conversationId: string;
  items: QueuedMessage[];
  onChange: (items: QueuedMessage[], revision: number) => void;
  onEdit: (item: QueuedMessage) => void;
  editor?: ReactNode;
}) {
  const pendingRef = useRef(new Set<string>());
  const [pending, setPending] = useState(new Set<string>());
  const [error, setError] = useState("");
  const mutate = async (
    id: string,
    method: "PATCH" | "DELETE",
    patch?: Partial<QueuedMessage>,
  ) => {
    if (pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    setPending(new Set(pendingRef.current));
    setError("");
    try {
      const result = await api<{ queue: QueuedMessage[]; revision: number }>(
        `/conversations/${conversationId}/queue/${id}`,
        { method, ...(patch ? { body: JSON.stringify(patch) } : {}) },
      );
      onChange(result.queue, result.revision);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : t("Could not update queue"),
      );
    } finally {
      pendingRef.current.delete(id);
      setPending(new Set(pendingRef.current));
    }
  };
  if (!items.length && !editor && !error) return null;
  return (
    <>
      <div
        className="mx-auto max-h-[30vh] w-full max-w-3xl shrink-0 space-y-2 overflow-y-auto px-4 pb-2"
        aria-label={t("Message queue")}
        role="region"
      >
        <div className="text-xs text-stone-500">
          {t("Message queue")} · {items.length}
        </div>
        {error && (
          <div role="alert" className="text-sm text-red-600">
            {error}
          </div>
        )}
        {orderedQueueItems(items).map((item, index) => (
          <div
            key={item.id}
            data-queued-message={item.id}
            className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#1b201e]"
          >
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate" title={item.content}>
                {item.content || t("Attachment-only message")}
              </span>
              {item.steer && (
                <span className="text-xs text-lumen-600">{t("Steer")}</span>
              )}
              <button
                type="button"
                className="button p-1"
                title={t("Edit queued message")}
                aria-label={t("Edit queued message")}
                disabled={pending.has(item.id)}
                onClick={() => onEdit(item)}
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                className={`button p-1 ${item.steer ? "text-lumen-600" : ""}`}
                title={t(
                  item.steer
                    ? "Stop steering this message"
                    : "Steer this message",
                )}
                aria-label={t(
                  item.steer
                    ? "Stop steering this message"
                    : "Steer this message",
                )}
                aria-pressed={Boolean(item.steer)}
                disabled={pending.has(item.id)}
                onClick={() =>
                  void mutate(item.id, "PATCH", { steer: !item.steer })
                }
              >
                <Send size={14} />
              </button>
              <button
                type="button"
                className="button p-1 text-stone-400 hover:text-red-600"
                title={t("Delete queued message")}
                aria-label={t("Delete queued message")}
                disabled={pending.has(item.id)}
                onClick={() => void mutate(item.id, "DELETE")}
              >
                <Trash2 size={14} />
              </button>
              {!item.error && (
                <span
                  className="text-xs text-stone-400"
                  aria-label={t("Queue position {{position}}", {
                    position: index + 1,
                  })}
                >
                  {index + 1}
                </span>
              )}
            </div>
            {!!item.attachmentIds?.length && (
              <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-stone-500">
                <Paperclip size={12} />
                {item.attachments?.length
                  ? item.attachments.map((file) => (
                      <span key={file.id}>{file.name}</span>
                    ))
                  : t("{{count}} attachments", {
                      count: item.attachmentIds.length,
                    })}
              </div>
            )}
            {item.error && (
              <div role="alert" className="mt-1 text-xs text-red-600">
                {t(item.error)}
              </div>
            )}
          </div>
        ))}
      </div>
      {editor}
    </>
  );
}
